import type { FC } from 'react'
import { Text, Flex, Skeleton } from '@chakra-ui/react'
import type { CoworkingSearchResult } from '@alexanderathoodly/data-models'
import { Heading } from '@chakra-ui/react'
import { getAreaTextWithPrefix } from './textutils'
import { useTranslation, Trans } from 'react-i18next'
import {
	getIsEnhancedSERPUrl,
	getEnhancedSERPOfficeType,
} from './enhancementutils'
import type { EnhancedSERPSubHeadingMap } from './enhancementutils'

const SUB_HEADINGS: EnhancedSERPSubHeadingMap = {
	stockholm: {
		office:
			'På den här sidan har vi samlat hela utbudet av lediga flexibla kontor i Stockholm - komplett med verifierade prisuppgifter, något som bara finns på yta.se. Med allt från privata kontorsrum till egna avdelningar eller helt egna kontor men med kortare avtal; finns det något att välja för bolag av alla storlekar. Utforska en varierad kontorsmarknad i Sveriges huvudstad med hundratals hyresvärdar och en stor spridning i pris och innehåll. Hittar du inte det du söker? Tveka inte att kontakta en av våra kontorsexperter för kostnadsfri rådgivning.',
		serviced_office:
			'yta.se gör det enkelt att hitta och jämföra bland alla kontorshotell i Stockholm genom att ge  tillgång till, och synliggöra, alla lediga kontorsrum och vad de kostar att hyra. Med över 200 kontorshotell i Stockholm vet vi av erfarenhet att det är tidskrävande att jämföra utan att förstå vad som finns tillgängligt som passar just ert teams behov. Vill du göra processen ännu enklare? Ta kostnadsfri hjälp av våra kontorsexperter som årligen hjälper hundratals företagare i Stockholm hitta rätt kontorshotell.',
		coworking_space:
			'Stockholm är en av Europas mest dynamiska städer för coworking spaces, en marknad som vuxit enormt kraftigt de senaste åren med över 200 anläggningar som följd. Oavsett om ni föredrar en kreativ hubb eller en lyxig förstaklass-upplevelse så finns det arbetsplatser som passar alla organisationer. På yta.se gör vi det enkelt att jämföra det dagsaktuella utbudet och priset av medlemskap, kontorsrum och egna avdelningar. Hittar du inte det du söker? Låt en av våra experter sy ihop en visningsrunda åt er - helt kostnadsfritt.',
	},
	göteborg: {
		office:
			'Letar du ett nytt kontor i Göteborg att hyra? På den här sidan kan du se alla lediga kontor med flexibla avtal i Göteborg just nu - bara hos oss på yta.se. Vi har kontor i alla populära områden som till exempel  Lindholmen, Inom Vallgraven och Nordstaden för att nämna några. Filtrera för hur många arbetsplatser ni behöver, välj vilka områden som är intressanta och vilka bekvämligheter ni vill ha. Svårt att bestämma dig? Kontakta en av våra kontorsexperter för kostnadsfri rådgivning där vi sållar marknaden tillsammans med dig och sammanställer relevanta förslag och underlag så att du kan lägga fokus på ditt eget arbete istället.',
		serviced_office:
			'Här kan du hitta och jämföra bland alla kontorshotell i Göteborg. Upptäck ett verifierat utbud av vakanta rum på kontorshotell i Göteborg som är lediga att hyra just nu eller inom ett par månader. Uppgifterna om rummen uppdateras dagligen av kontorshotellen själva, vilket gör att det du ser är faktiska lediga alternativ just nu som är möjliga att hyra - unikt på yta.se. Dessutom kan du se exakt vilka kontor det är direkt på våra interaktiva planritningar, vad hyrorna är och vilka bekvämligheter som ingår hos respektive kontorshotell.',
		coworking_space:
			'Letar du coworking i Göteborg? Välkommen till yta.se - din samlingsplats för alla coworking spaces i Göteborg. Här har vi samlat, digitaliserat och gjort hela utbudet sökbart för att göra det så enkelt som möjligt för dig att hitta rätt coworking space. Vi vet hur svårt och snårigt det kan vara att jämföra alternativ, boka visningar, samla in offerter, förhandla och allt som hör till att hyra ett coworking space. Därför finns vi, för att underlätta processen och vi kan även göra hela jobbet tillsammans med er, där vi utser en personlig kontorsexpert som kommer driva på hela processen tillsammans med er - helt kostnadsfritt.',
	},
	malmö: {
		office:
			'Hyr lediga kontor enkelt i Malmö. Här kan du se samtliga lediga kontor i Malmö just nu samt all information kring vad som ingår, hyresnivåer, avtalstider och planritningar. Använd kartvyn för att enkelt se var alla kontor ligger och filtrera fram vilka bekvämligheter ni behöver och vill ha. När du hittat ett eller fler alternativ du vill titta på kan du enkelt boka en visning direkt på respektive kontor, eller ta kostnadsfri hjälp från våra kontorsexperter som hjälper er i hela processen med allt från att hitta rätt lokal till att boka visningar och driva på förhandlingar.',
		serviced_office:
			'Letar du kontorshotell i Malmö? Jämför bland hela utbudet och boka visningar enkelt hos oss på yta.se. Vi samlar i realtid in all information från operatörerna, så som priser, vakanser och vad som ingår - och gör det synligt och sökbart för dig som letar kontorshotell i Malmö. Filtrera på de bekvämligheter ni vill ha, typ av kontor, hur många arbetsplatser ni behöver, vad ni har för budget och vilket område ni vill sitta i. Resultatet du får upp är faktiska lediga rum på kontorshotellet just nu. Jämför bland resultatet och boka visningar enkelt själv, eller ta kostnadsfri hjälp från en av våra kontorsexperter.',
		coworking_space:
			'Vill du hyra coworking i Malmö? Malmö kryllar av spännande, lyxiga, prisvärda och häftiga coworking spaces. Vare sig du söker en arbetsplats eller ett eget våningsplan med service och flexibelt avtal för ert nya huvudkontor, finns det alltid något bland Malmös coworking spaces som passar. På den här sidan samlar vi alla lediga alternativ på coworking spaces i Malmö, så att du enkelt kan hitta och jämföra bland de olika alternativen. Vi har digitaliserat alla planritningar, fotat anläggningarna, lagt in priser, vad som ingår och hur avtalsformerna ser ut. Välkommen till yta.se - din partner för att hitta och jämföra coworking spaces i Malmö.',
	},
	'stockholm/östermalm': {
		office:
			'Östermalm må vara en liten stadsdel sett till yta men är en av de mest prestigefyllda kontorsmarknaderna i Europa. Oavsett om det är pampiga sekelskiftesfastigheter kring Stureplan eller extravaganta takterrasser på Strandvägen är en sak säker, kontoren på Östermalm är några av de mest eftertraktade i Stockholm. På yta.se samlar vi hela utbudet av inflyttningsklara kontor på Östermalm där någon annan står för servicen. Vill du spara tid? Få kostnadsfri hjälp av en av våra kontorsexperter och bli inbokad på en visningsrunda baserat på era behov.',
		serviced_office:
			'Östermalm är en av Sveriges kontorshotellstätaste stadsdelar, med över 20 olika kontorshotell med tusentals kontorsalternativ på en liten yta. Här samlas både de som söker efter Stockholms lyxigaste kontor och de som vill åt centrum av näringslivet för att växa sin egen business. På yta.se gör vi det så enkelt som möjligt att förstå om ett kontorshotell på Östermalm är rätt för er genom att presentera vakanta rum komplett med prisbild och allt nödvändigt underlag. Hittar du inte det du söker? Prata med en av våra Östermalmsexperter för kostnadsfri rådgivning kring vad som passar just ert team bäst.',
		coworking_space:
			'Östermalms populäraste coworking spaces har alla en sak gemensamt - de delar ett av Stockholms mest prestigefyllda lägen och de sparar inte in på vad som ingår. Letar du efter en inspirerande arbetsplats där du kan inspireras av likasinnade? På yta.se samlar vi alla coworking spaces på Östermalm och ger dig direkt access till månadshyra samt vilka tjänster som ingår så att du kan göra en enkel jämförelse.',
	},
	'stockholm/södermalm': {
		office:
			'Letar du lediga kontor på Södermalm? På yta.se samlar vi utbudet av kontor med flexibla avtal och service som tillval. Kontoren du ser uppdateras av fastighetsägarna och uthyrarna själva i realtid, vilket betyder att kontoren du ser är lediga kontor just nu, eller i vissa fall inom 3 månader. Vi har digitaliserat alla planritningar, fotat själva och samlat in samt gjort sökbart allt från hyror till service och avtalsvillkor. På vår sida för lediga kontor på Södermalm kan du alltså se exakt vad ett kontor kostar, var det ligger på en planritning, vad som ingår och vilka avtalsvillkor du kan välja på. Du kan också enkelt boka en visning själv direkt på respektive kontor.',
		serviced_office:
			'Letar du efter kontorshotell på Södermalm? Då har du kommit rätt. På den här sidan har vi samlat alla kontorshotell på Södermalm med realtidsdata på vakanser, priser, service och allt du behöver för att hitta rätt kontorshotell på Söder. Som du kommer märka finns det ett varierat utbud av kontorshotell på Söder, där det delvis finns några av Stockholms billigaste lösningar, liksom några av de dyraste. Om du känner att projektet är för stort kan du alltid få kostnadsfri hjälp av oss, där vi utser en kontorsexpert som följer med dig och vägleder i hela processen, från att förklara koncept, ta fram förslag på kontorshotell, boka visningar och sköta förhandling.',
		coworking_space:
			'Hitta och jämför Coworking Spaces på Södermalm - enkelt hos oss på yta.se. Vi samarbetar med hela utbudet av coworking spaces på Söder och har digitaliserat processen att hitta rätt alternativ. I det arbetet har vi digitaliserat alla planritningar, strukturerat alla priser, service, adresser och tagit våra egna bilder. Allt för att du ska ha så bra underlag som bara möjligt för att kunna fatta ett välgrundat beslut. Att hitta rätt coworking kan upplevas som en överväldigande uppgift då det finns så otroligt mycket att välja på - därför erbjuder vi helt kostnadsfri rådgivning i hela processen, så att ni kan fokusera på er kärnverksamhet istället för att ringa runt och leta kontor.',
	},
}

function useHeading(
	pageType: string,
	area: string | undefined,
	areaWithCity: string | undefined,
	result: CoworkingSearchResult | undefined
): { heading: string | undefined } {
	const { t } = useTranslation()

	if (area == undefined || areaWithCity == undefined) {
		return { heading: undefined }
	}

	const solutionCount = result ? result.all_solutions_count : 0

	const noResultsHeading = (type: string) =>
		t('Lediga {{type}} att hyra {{area_with_city}}', {
			type,
			area_with_city: areaWithCity,
		})

	const getHeading = () => {
		if (pageType === t('lediga-kontorshotell', { ns: 'url' })) {
			return solutionCount > 0
				? t(
						'{{solution_count}} lediga rum på kontorshotell att hyra {{area_with_city}}',
						{
							solution_count: solutionCount,
							area_with_city: areaWithCity,
						}
				  )
				: noResultsHeading(t('Kontorshotell').toLocaleLowerCase())
		}
		if (pageType === t('coworking-spaces', { ns: 'url' })) {
			return solutionCount > 0
				? t(
						'{{solution_count}} lediga rum på coworking spaces att hyra {{area_with_city}}',
						{
							solution_count: solutionCount,
							area_with_city: areaWithCity,
						}
				  )
				: noResultsHeading(t('Coworking spaces').toLocaleLowerCase())
		}
		// /lediga-kontor/
		return solutionCount > 0
			? t('{{solution_count}} lediga kontor att hyra {{area_with_city}}', {
					solution_count: solutionCount,
					area_with_city: areaWithCity,
			  })
			: noResultsHeading(t('Kontor').toLocaleLowerCase())
	}

	return { heading: getHeading() }
}
function useSubHeading(
	areaUrl: string | undefined,
	pageType: string,
	page: number | undefined
): string | null {
	const { t } = useTranslation()

	const isEnhancedArea = getIsEnhancedSERPUrl(areaUrl)
	const officeType = getEnhancedSERPOfficeType(pageType, t)
	return isEnhancedArea && areaUrl && officeType && !page
		? SUB_HEADINGS[areaUrl][officeType]
		: null
}

type TopSectionProps = {
	page: number | undefined
	pageType: string | undefined
	result: CoworkingSearchResult | undefined
	neighborhood: string | undefined
	city: string | undefined
	mapMode: boolean
	isLoading: boolean
	areaUrl: string | undefined
}

export const TopSection: FC<TopSectionProps> = ({
	page,
	pageType = '',
	result,
	neighborhood,
	city,
	mapMode,
	isLoading,
	areaUrl,
}) => {
	const { t } = useTranslation()
	const area = getAreaTextWithPrefix(neighborhood, city, false, t)
	const areaWithCity = getAreaTextWithPrefix(neighborhood, city, true, t)

	const { heading } = useHeading(pageType, area, areaWithCity, result)
	const subHeading = useSubHeading(areaUrl, pageType, page)

	return (
		<>
			<Flex direction="column" mb="10px">
				<Heading
					as="h1"
					fontSize={
						mapMode ? { base: 'lg', md: '2xl' } : { base: 'lg', md: '4xl' }
					}
					color="grey.800"
					fontWeight="semibold"
					mb={mapMode ? 3 : 1}
				>
					<Skeleton
						w={{ base: 6, md: 9, lg: 14 }}
						h={{ base: 3, md: 6, lg: 7 }}
						display={result ? 'none' : 'inline-block'}
					/>{' '}
					{heading}
					<Skeleton
						w={{ base: 9, md: 14, lg: 20 }}
						h={{ base: 3, md: 6, lg: 7 }}
						display={city == undefined ? 'inline-block' : 'none'}
					/>{' '}
				</Heading>
				{subHeading && (
					<Text
						color="grey.500"
						mt={1}
						display={
							mapMode ||
							(!isLoading && result && result?.all_solutions_count === 0)
								? 'none'
								: 'flex'
						}
					>
						<Trans i18nKey={subHeading}>{subHeading}</Trans>
					</Text>
				)}
			</Flex>
		</>
	)
}
